import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdminSettlements } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const STATUS_COLOR = { success: "#2ec4b6", processing: "#f4a261", pending: "#f4a261" };

export default function Settlements() {
  const dispatch = useDispatch();
  const settlements = useSelector((s) => s.profile.adminSettlements);
  const meta = useSelector((s) => s.profile.adminSettlementsMeta);

  const [page, setPage] = useState(1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = (p = page) => {
    const params = { page: p, perPage: 50 };
    if (from) params.from = from;
    if (to) params.to = to;
    dispatch(getAdminSettlements(params));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(1); }, [dispatch]);

  const fmtMoney = (k) => k != null ? `₦${(k / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}` : "—";
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const pageTotal = settlements.reduce((s, t) => s + (t.settled_by?.amount || t.amount || 0), 0);

  return (
    <div style={S.root}>
      <div style={S.pageHeader}>
        <div>
          <h2 style={S.heading}>Settlements</h2>
          <p style={S.headingSub}>{meta?.total || settlements.length} settlement records</p>
        </div>
        <div style={S.summaryPill}>
          <i className="bi bi-cash-stack" style={{ color: PRIMARY, marginRight: 6 }} />
          <span style={{ fontSize: 12, color: "#555" }}>This page total:</span>
          <span style={{ fontWeight: 700, color: BG, marginLeft: 6 }}>{fmtMoney(pageTotal)}</span>
        </div>
      </div>

      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>From</label>
          <input style={S.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div style={S.filterGroup}>
          <label style={S.filterLabel}>To</label>
          <input style={S.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
          <button style={S.btn} onClick={() => { setPage(1); load(1); }}>Apply Filter</button>
          <button style={S.btnSecondary} onClick={() => { setFrom(""); setTo(""); setPage(1); dispatch(getAdminSettlements({ page: 1, perPage: 50 })); }}>Reset</button>
        </div>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {["ID", "Amount", "Status", "Bank", "Account Name", "Account #", "Date"].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {settlements.length === 0 ? (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#bbb", padding: 32 }}>No settlements found</td></tr>
            ) : settlements.map((s, i) => (
              <tr key={s.id || i} style={S.tr}>
                <td style={S.td}><span style={S.mono}>{s.id}</span></td>
                <td style={{ ...S.td, fontWeight: 700, color: BG }}>{fmtMoney(s.settled_by?.amount || s.amount)}</td>
                <td style={S.td}>
                  <span style={{ background: (STATUS_COLOR[s.status] || "#888") + "18", color: STATUS_COLOR[s.status] || "#888", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>
                    {s.status || "—"}
                  </span>
                </td>
                <td style={S.td}>{s.bank_name || "—"}</td>
                <td style={S.td}>{s.account_name || "—"}</td>
                <td style={S.td}><span style={S.mono}>{s.account_number || "—"}</span></td>
                <td style={{ ...S.td, color: "#888" }}>{fmtDate(s.settlement_date || s.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={S.pagination}>
        <button style={S.pgBtn} disabled={page <= 1} onClick={() => { const p = page - 1; setPage(p); load(p); }}>‹ Previous</button>
        <span style={{ color: "#888", fontSize: 13 }}>Page {page}</span>
        <button style={S.pgBtn} onClick={() => { const p = page + 1; setPage(p); load(p); }}>Next ›</button>
      </div>
    </div>
  );
}

const S = {
  root: { paddingBottom: 40 },
  pageHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  heading: { fontSize: 22, fontWeight: 800, color: BG, margin: 0 },
  headingSub: { fontSize: 13, color: "#888", marginTop: 4 },
  summaryPill: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 10, padding: "10px 18px", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" },
  filterBar: { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20, alignItems: "flex-end" },
  filterGroup: { display: "flex", flexDirection: "column", gap: 4 },
  filterLabel: { fontSize: 11, fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: 0.5 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "8px 12px", fontSize: 13, color: "#333", outline: "none", background: "#fafafa" },
  btn: { background: BG, border: "none", borderRadius: 8, color: "#fff", fontWeight: 600, padding: "8px 20px", cursor: "pointer", fontSize: 13 },
  btnSecondary: { background: "#f5f5f5", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#666", fontWeight: 500, padding: "8px 16px", cursor: "pointer", fontSize: 13 },
  tableWrap: { background: "#fff", borderRadius: 10, overflowX: "auto", boxShadow: "0 2px 10px rgba(0,0,0,0.06)", WebkitOverflowScrolling: "touch" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { background: BG, color: "#fff", padding: "11px 14px", textAlign: "left", fontSize: 12, fontWeight: 600 },
  tr: { borderBottom: "1px solid #f5f5f5" },
  td: { padding: "11px 14px", fontSize: 13, color: "#555", verticalAlign: "middle" },
  mono: { fontFamily: "monospace", fontSize: 12, color: "#888" },
  pagination: { display: "flex", alignItems: "center", gap: 16, marginTop: 16, justifyContent: "flex-end" },
  pgBtn: { background: "#fff", border: "1.5px solid #e0e0e0", borderRadius: 8, color: "#555", padding: "7px 18px", cursor: "pointer", fontSize: 13, fontWeight: 500 },
};
